import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import { de as deLocale, enGB as enLocale } from "date-fns/locale";
import { format } from "date-fns";

import { PageHeader } from "../../../components/admin/PageHeader";
import { DataTable } from "../../../components/admin/DataTable";
import { FormModal } from "../../../components/admin/FormModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { DatePicker } from "../../../components/ui/DatePicker";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useCrudList } from "../../../hooks/useCrudList";
import { holidaysService } from "../services/holidays";

function localizedDate(iso, lang) {
  if (!iso) return "";
  const locale = lang?.startsWith("en") ? enLocale : deLocale;
  return format(parseISO(iso), "EEE d. MMM yyyy", { locale });
}

export default function HolidaysPage() {
  const { t, i18n } = useTranslation();
  const crud = useCrudList({
    service: holidaysService,
    defaultSort: { key: "date", dir: "asc" },
    sessionKey: "holidays.pageSize",
  });
  const [formValue, setFormValue] = useState({ name: "", date: "", region: "DE" });

  useEffect(() => {
    if (!crud.modalOpen) return;
    if (crud.editingRow) {
      setFormValue({
        name: crud.editingRow.name || "",
        date: crud.editingRow.date || "",
        region: crud.editingRow.region || "DE",
      });
    } else {
      setFormValue({ name: "", date: "", region: "DE" });
    }
  }, [crud.modalOpen, crud.editingRow]);

  const columns = [
    { key: "name", labelKey: "admin.holidays.col_name", sortable: true },
    {
      key: "date",
      labelKey: "admin.holidays.col_date",
      sortable: true,
      className: "w-56 whitespace-nowrap",
      render: (r) => (
        <span className="font-mono text-sm text-text-primary">
          {localizedDate(r.date, i18n.language)}
        </span>
      ),
    },
    {
      key: "region",
      labelKey: "admin.holidays.col_region",
      className: "w-24",
      render: (r) => (
        <span className="font-mono text-xs uppercase text-text-secondary">
          {r.region || "DE"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="holidays-page">
        <PageHeader
          title={t("admin.holidays.title")}
          subtitle={t("admin.holidays.subtitle")}
          actionLabel={t("admin.common.add_new")}
          onAction={crud.openCreate}
          actionTestId="holidays-add-new"
        />
        <DataTable
          columns={columns}
          items={crud.items}
          loading={crud.loading}
          total={crud.total}
          page={crud.page}
          pageSize={crud.pageSize}
          onPageChange={crud.setPage}
          onPageSizeChange={crud.setPageSize}
          sortKey={crud.sortKey}
          sortDir={crud.sortDir}
          onSortChange={crud.changeSort}
          searchValue={crud.searchValue}
          onSearchChange={crud.setSearchValue}
          onEdit={crud.openEdit}
          onDelete={crud.askDelete}
          entityTestId="holidays"
        />
      </div>

      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        mode={crud.editingRow ? "edit" : "create"}
        entityLabel={t("admin.holidays.entity_singular")}
        saving={crud.saving}
        onSubmit={() =>
          crud.save({
            name: formValue.name.trim(),
            date: formValue.date,
            region: (formValue.region || "DE").trim().toUpperCase(),
          })
        }
        testId="holidays-form"
        submitTestId="holidays-form-submit"
        cancelTestId="holidays-form-cancel"
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="hol-name">{t("admin.holidays.field_name")}</Label>
            <Input
              id="hol-name"
              value={formValue.name}
              onChange={(e) => setFormValue({ ...formValue, name: e.target.value })}
              required
              minLength={2}
              maxLength={100}
              autoFocus
              data-testid="holidays-form-name"
              className="bg-surface border-border-subtle"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.holidays.field_date")}</Label>
            <DatePicker
              value={formValue.date}
              onChange={(d) => d && setFormValue({ ...formValue, date: d })}
              required
              testId="holidays-form-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hol-region">{t("admin.holidays.field_region")}</Label>
            <Input
              id="hol-region"
              value={formValue.region}
              onChange={(e) => setFormValue({ ...formValue, region: e.target.value })}
              minLength={2}
              maxLength={10}
              data-testid="holidays-form-region"
              className="bg-surface border-border-subtle font-mono uppercase"
            />
          </div>
        </div>
      </FormModal>

      <ConfirmDeleteModal
        open={Boolean(crud.deleteRow)}
        onClose={crud.cancelDelete}
        onConfirm={crud.confirmDelete}
        name={crud.deleteRow?.name || ""}
        busy={crud.deleting}
        testId="holidays-delete"
      />
    </>
  );
}
